import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, StatusDot } from '@/components/atoms';
import { useExecutions, useWorkflows } from '@/hooks/useWorkflows';

import styles from './Alerts.module.css';

const READ_KEY = 'wiredesk-alerts-read';

function loadRead(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function saveRead(set: Set<string>) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

export function Alerts() {
  const { data: errors, isLoading } = useExecutions({ status: 'error', limit: 100 });
  const { data: workflows } = useWorkflows();
  const [showRead, setShowRead] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState<string | 'all'>('all');
  const [read, setRead] = useState<Set<string>>(loadRead);

  useEffect(() => {
    saveRead(read);
  }, [read]);

  const workflowNameById = useMemo(() => {
    const m = new Map<string, string>();
    workflows?.forEach((w) => m.set(w.id, w.name));
    return m;
  }, [workflows]);

  const filtered = useMemo(() => {
    if (!errors) return [];
    return errors.filter((e) => {
      if (workflowFilter !== 'all' && e.workflow_id !== workflowFilter) return false;
      if (!showRead && read.has(e.id)) return false;
      return true;
    });
  }, [errors, showRead, workflowFilter, read]);

  const markAllRead = () => {
    if (!errors) return;
    const next = new Set(read);
    errors.forEach((e) => next.add(e.id));
    setRead(next);
  };

  const markRead = (id: string) => {
    const next = new Set(read);
    next.add(id);
    setRead(next);
  };

  const unreadCount = errors ? errors.filter((e) => !read.has(e.id)).length : 0;

  // Workflows that have errors — for filter dropdown
  const workflowsWithErrors = useMemo(() => {
    if (!errors) return [];
    const seen = new Map<string, number>();
    errors.forEach((e) => seen.set(e.workflow_id, (seen.get(e.workflow_id) ?? 0) + 1));
    return Array.from(seen.entries())
      .map(([id, count]) => ({
        id,
        name: workflowNameById.get(id) ?? id,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [errors, workflowNameById]);

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>Alertas</h1>
        <Badge tone="crit" size="sm">
          {unreadCount} não lidos
        </Badge>
        {errors && (
          <span className={styles.totalBadge}>
            {filtered.length} / {errors.length} mostrados
          </span>
        )}
        {unreadCount > 0 && (
          <button className={styles.markAllBtn} onClick={markAllRead}>
            Marcar todos como lidos
          </button>
        )}
      </header>

      <div className={styles.toolbar}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={showRead}
            onChange={(e) => setShowRead(e.target.checked)}
          />
          Mostrar lidos
        </label>

        {workflowsWithErrors.length > 0 && (
          <select
            value={workflowFilter}
            onChange={(e) => setWorkflowFilter(e.target.value)}
            className={styles.select}
          >
            <option value="all">Todos os workflows ({errors?.length ?? 0})</option>
            {workflowsWithErrors.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.count})
              </option>
            ))}
          </select>
        )}
      </div>

      {isLoading && <p className={styles.muted}>A carregar…</p>}

      {errors && filtered.length === 0 && (
        <div className={styles.empty}>
          <StatusDot state="ok" />
          <p>
            {errors.length === 0
              ? 'Sem erros nas últimas execuções.'
              : 'Tudo lido. Activa "Mostrar lidos" para rever.'}
          </p>
        </div>
      )}

      <ul className={styles.list}>
        {filtered.map((e) => {
          const isRead = read.has(e.id);
          const name = workflowNameById.get(e.workflow_id) ?? e.workflow_id;
          return (
            <li
              key={e.id}
              className={`${styles.row} ${isRead ? styles.rowRead : ''}`}
            >
              <StatusDot state="crit" />
              <div className={styles.body}>
                <Link
                  to={`/workflows/${e.workflow_id}/execucoes/${e.id}`}
                  className={styles.link}
                >
                  {name}
                </Link>
                <div className={styles.meta}>
                  <span>execução #{e.id}</span>
                  <span>{new Date(e.started_at).toLocaleString('pt-BR')}</span>
                  {e.mode && <span>modo: {e.mode}</span>}
                </div>
              </div>
              {!isRead && (
                <button className={styles.smallBtn} onClick={() => markRead(e.id)}>
                  Marcar lido
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
